'use client';

import React, {
  useEffect,
  useRef,
  useId,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { TrixEditorProps } from './TrixEditor.types';
import styles from './TrixEditor.module.css';

let trixLoaded = false;
let loadingPromise: Promise<void> | null = null;

const loadTrix = (): Promise<void> => {
  if (trixLoaded) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve) => {
    // Load CSS
    if (!document.querySelector('link[href*="trix"]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/trix@2/dist/trix.css';
      document.head.appendChild(css);
    }

    // Load JS
    if (
      !document.querySelector('script[src*="trix"]') &&
      !(window as any).Trix
    ) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/trix@2/dist/trix.umd.min.js';
      script.onload = () => {
        trixLoaded = true;
        resolve();
      };
      document.head.appendChild(script);
    } else if ((window as any).Trix) {
      trixLoaded = true;
      resolve();
    } else {
      resolve();
    }
  });

  return loadingPromise;
};

export const TrixEditor = ({
  isDisabled,
  isResponsive,
  placeholder = 'Message',
  setValue,
  value,
}: TrixEditorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLElement>(null);
  const lastValueRef = useRef(value);
  const changeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const inputId = useId();
  const [isLoaded, setIsLoaded] = useState(trixLoaded);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    loadTrix().then(() => {
      setIsLoaded(true);
    });
  }, []);

  const debouncedSetValue = useCallback(
    (html: string) => {
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);
      }

      changeTimeoutRef.current = setTimeout(() => {
        setValue(html);
      }, 100);
    },
    [setValue],
  );

  useEffect(() => {
    const handleChange = () => {
      const editor = editorRef.current as any;
      if (!editor?.editor) return;

      const serializedHtml = editor.innerHTML;
      debouncedSetValue(serializedHtml);
    };

    // TODO: Support image paste if we are using this for template editing, email content, etc.
    const handlePaste = (event: any) => {
      const attachment = event.attachment;

      let isImage = false;

      if (
        (attachment && !attachment.file && attachment.attachment) || // pasted image
        attachment?.file?.type?.startsWith('image/') // dragged image file
      ) {
        isImage = true;
      }

      if (isImage) {
        event.preventDefault();

        const editor = editorRef.current as any;
        if (editor?.editor) {
          attachment.remove();
          editor.editor.insertHTML(
            '<i><strong>(⚠️: Pasting images is not supported, please upload images via the "Upload" button instead)</strong></i>',
          );
        }
      }
    };

    const editor = editorRef.current;
    if (!editor) return;

    editor.addEventListener('trix-change', handleChange);
    editor.addEventListener('trix-attachment-add', handlePaste);

    return () => {
      editor.removeEventListener('trix-change', handleChange);
      editor.removeEventListener('trix-attachment-add', handlePaste);
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);
      }
    };
  }, [debouncedSetValue]);

  const shouldUpdateEditor = useMemo(() => {
    return lastValueRef.current !== value;
  }, [value]);

  useEffect(() => {
    if (!shouldUpdateEditor) return;
    lastValueRef.current = value;

    const input = inputRef.current;
    const editor = editorRef.current as any;

    if (editor?.contains(document.activeElement)) return;

    if (input) {
      input.value = value;
    }

    if (!editor?.editor) return;

    const htmlToLoad = value === '' || value === '<div><br></div>' ? '' : value;
    editor.editor.loadHTML(htmlToLoad);
  }, [value, shouldUpdateEditor]);

  return (
    <div className={styles.trixEditorContainer} data-responsive={isResponsive}>
      <input ref={inputRef} type="hidden" id={inputId} defaultValue={value} />
      {React.createElement('trix-editor', {
        ref: editorRef,
        input: inputId,
        disabled: !isLoaded || isDisabled,
        placeholder,
      })}
    </div>
  );
};
